import { asText, isFilled, type RichTextField } from '@prismicio/client';
import { cn } from '@/lib/utils';

export type SplitColorTitleLayout = 'inline' | 'stacked';

type Theme = 'light' | 'dark';

type Props = {
  /** Green-toned lead phrase. */
  lead?: RichTextField | null;
  /** White-on-dark / near-black-on-light body phrase. */
  body?: RichTextField | null;
  /** `inline` = one line, `stacked` = two lines (lead over body). */
  layout: SplitColorTitleLayout;
  theme: Theme;
  className?: string;
};

/**
 * Two-tone section heading used by `SideHeaderCardsRow`: a green lead phrase
 * followed by a body phrase in the theme's dominant text color. Content authors
 * pick the layout — `inline` renders both phrases on the same line, `stacked`
 * breaks between them.
 *
 * Kept intentionally separate from the shell so future variants of the same
 * slice can pass a different heading treatment.
 */
export function SplitColorTitle({
  lead,
  body,
  layout,
  theme,
  className,
}: Props) {
  const hasLead = isFilled.richText(lead);
  const hasBody = isFilled.richText(body);

  if (!hasLead && !hasBody) {
    return null;
  }

  const isDark = theme === 'dark';
  // Same tokens as `HorizontalContentCards` so the family reads as one system.
  const leadClass = isDark ? 'text-[#05f273]' : 'text-[#aaa]';
  const bodyClass = isDark ? 'text-white' : 'text-[#041118]';
  // A trailing space keeps the two phrases readable when they meet inline.
  const inlineSeparator = layout === 'inline' && hasLead && hasBody ? ' ' : '';

  return (
    <h2
      className={cn(
        'text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]',
        className,
      )}
    >
      {hasLead && (
        <span className={leadClass}>
          {asText(lead)}
          {inlineSeparator}
        </span>
      )}
      {hasLead && hasBody && layout === 'stacked' && <br />}
      {hasBody && <span className={bodyClass}>{asText(body)}</span>}
    </h2>
  );
}
